// A unit is one straight line through its category's base unit: how big one of them is, and where its zero sits.
// Only the temperatures have a zero anywhere but absolute, and one number each is what keeps them in the same table
// as everything else rather than in a conversion of their own.
export interface Unit {
  // What the share link carries, so a label can be reworded without ageing every link that named it.
  id: string;
  name: string;
  symbol: string;
  // How many of the base unit one of these makes.
  factor: number;
  // Where this scale's zero falls on the base one, in base units.
  offset?: number;
}

export interface Category {
  id: string;
  label: string;
  // Smallest first, so the table reads as a scale climbing rather than as a list somebody typed in.
  units: Unit[];
  // What an amount is read as when the category is picked: the tables open on their smallest unit, which is rarely
  // the one anybody means by it.
  defaultUnit: string;
}

// Through the base unit rather than a table of pairs: fifteen units are a hundred and five pairs, and one of them
// would be wrong. Every factor is measured against the same base, so the two halves cannot disagree.
export function convert(value: number, from: Unit, to: Unit): number {
  const base = value * from.factor + (from.offset ?? 0);
  const converted = (base - (to.offset ?? 0)) / to.factor;
  return Math.abs(converted) < cancelledToNothing(from, to) ? 0 : converted;
}

// Absolute zero written on a scale whose own zero is elsewhere subtracts two numbers a few hundred wide from each
// other, and what a double has left of that is a fraction of a trillionth rather than the nothing it is. The floor
// is measured off the offsets, so it is zero for every category but temperature and nothing else can fall through it.
function cancelledToNothing(from: Unit, to: Unit): number {
  return Math.max(Math.abs(from.offset ?? 0), Math.abs(to.offset ?? 0)) * 1e-12 / to.factor;
}
